import { map, chain, fold } from 'fp-ts/lib/Either';
import { identity } from 'fp-ts/lib/function';
import { pipe } from 'fp-ts/lib/pipeable';
import { inverse } from '../AffineTransform';
import { Vector3D } from '../Vector';
function testAxisIntersection(axis, polyA, polyB) {
    const projectedA = polyA.map(v => axis.dot(Vector3D.fromPosition(v)));
    const projectedB = polyB.map(v => axis.dot(Vector3D.fromPosition(v)));
    const minA = Math.min(...projectedA);
    const minB = Math.min(...projectedB);
    const maxA = Math.max(...projectedA);
    const maxB = Math.max(...projectedB);
    const overlapA = maxB - minA;
    const overlapB = maxA - minB;
    return overlapA > 0 && overlapB > 0;
}
/**
 * test each axis for an intersection
 */
function testIntersection(axes, polyA, polyB) {
    // if ANY projected axis is not intersecting, then there is no collision
    return axes.every(axis => testAxisIntersection(axis, polyA, polyB));
}
export class OrientedBoundingBox {
    _aabb;
    _transform;
    constructor(axisAlignedBoundingBox, transform) {
        this._aabb = axisAlignedBoundingBox;
        this._transform = transform;
    }
    max() {
        return this._aabb.max();
    }
    min() {
        return this._aabb.min();
    }
    affineTransform() {
        return this._transform;
    }
    rawAxisAlignedBoundingBox() {
        return this._aabb;
    }
    corners() {
        return this._transform.cornersFromAxisAlignedBoundingBox(this._aabb);
    }
    xzCorners() {
        return this._transform.xzVerticesFromAxisAlignedBoundingBox(this._aabb);
    }
    xyCorners() {
        return this._transform.xyVerticesFromAxisAlignedBoundingBox(this._aabb);
    }
    namedCorners() {
        return pipe(this.corners(), map(corners => ({
            'left-bottom-rear': corners[0],
            'right-bottom-rear': corners[1],
            'left-top-rear': corners[2],
            'right-top-rear': corners[3],
            'left-bottom-front': corners[4],
            'right-bottom-front': corners[5],
            'left-top-front': corners[6],
            'right-top-front': corners[7],
        })));
    }
    axisAlignedBoundingBox() {
        return this._transform.axisAlignedBoundingBox(this._aabb);
    }
    centre() {
        return this._transform.position(this._aabb.centre());
    }
    union(right) {
        return pipe(right.axisAlignedBoundingBox(), chain(rightAABB => pipe(this._transform, inverse, chain(inverseTransform => inverseTransform.axisAlignedBoundingBox(rightAABB)))), map(transformedAABB => this._aabb.union(transformedAABB)), map(unionAABB => new OrientedBoundingBox(unionAABB, this._transform)));
    }
    axes() {
        return this._transform.axes();
    }
    isCollidingWith(x) {
        return pipe(this.axes(), chain(axes => pipe(x.axes(), map(as => [as.x, as.y, as.z]
            .concat([axes.x, axes.y, axes.z])
            .concat([as.x.cross(axes.x), as.x.cross(axes.y), as.x.cross(axes.z)])
            .concat([as.y.cross(axes.x), as.y.cross(axes.y), as.y.cross(axes.z)])
            .concat([as.z.cross(axes.x), as.z.cross(axes.y), as.z.cross(axes.z)])
            .filter(v => v.mag() > 1e-6)))), chain(axes => {
            return pipe(this.corners(), chain(polyA => pipe(x.corners(), map(polyB => [polyA, polyB]))), map(([polyA, polyB]) => testIntersection(axes, polyA, polyB)));
        }), 
        // TODO: should a failure count as intersection or not?
        fold(() => false, identity));
    }
}
//# sourceMappingURL=OrientedBoundingBox.js.map